import { useEffect, useState } from "react";
import { X, Download, RefreshCw, FileDown } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

/**
 * Spec utilisateur fork 25 — modal affichant la timeline append-only
 * (audit log) d'un visiteur OU d'un établissement. Chaque ligne est un
 * événement discret avec son propre bouton « Télécharger » (PDF).
 *
 * Props :
 *  - kind: "user" | "commerce"
 *  - entityId: id du visiteur ou de l'établissement
 *  - title: titre du modal (nom + email/adresse)
 *  - onClose: callback
 */
export default function HistoryModal({ kind, entityId, title, onClose }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const base = kind === "user" ? "/admin/users" : "/admin/commerces";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${base}/${entityId}/history`);
      setEvents(data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityId]);

  const _downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadAll = async () => {
    try {
      const { data } = await api.get(`${base}/${entityId}/history/download`, { responseType: "blob" });
      const prefix = kind === "user" ? "historique-visiteur" : "historique-etablissement";
      _downloadBlob(new Blob([data], { type: "application/pdf" }), `${prefix}-${entityId.slice(0, 8)}.pdf`);
      toast.success("Historique téléchargé");
    } catch {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const downloadOne = async (eventId) => {
    setDownloadingId(eventId);
    try {
      const { data } = await api.get(`/admin/history/events/${eventId}/download`, { responseType: "blob" });
      _downloadBlob(new Blob([data], { type: "application/pdf" }), `evenement-${eventId.slice(0, 8)}.pdf`);
    } catch {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
      data-testid="history-modal-backdrop"
    >
      <div
        className="bg-white border-2 border-brand max-w-3xl w-full rounded-xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="history-modal"
        role="dialog"
        aria-labelledby="history-modal-title"
      >
        <div className="px-5 py-4 border-b border-border bg-surface/60 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="overline text-[10px] text-brand mb-0.5">{kind === "user" ? "Historique du visiteur" : "Historique de l'établissement"}</p>
            <h3 id="history-modal-title" className="font-display text-lg font-black text-ink leading-tight truncate" data-testid="history-modal-title">
              {title || "Historique"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-subtle hover:text-ink p-1"
            aria-label="Fermer"
            data-testid="history-modal-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-border bg-surface/40 flex flex-wrap gap-2">
          <button
            onClick={downloadAll}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-ink hover:border-brand text-xs font-bold uppercase tracking-wider"
            data-testid="history-download-btn"
          >
            <Download size={14} /> Télécharger tout (PDF)
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-border text-subtle hover:border-ink text-xs font-bold uppercase tracking-wider ml-auto"
            data-testid="history-refresh-btn"
          >
            <RefreshCw size={14} /> Rafraîchir
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 bg-white" data-testid="history-timeline">
          {loading && !events && (
            <p className="text-sm text-subtle italic">Chargement…</p>
          )}
          {events && events.length === 0 && (
            <p className="text-sm text-subtle italic" data-testid="history-empty">
              Aucun événement enregistré pour ce {kind === "user" ? "visiteur" : "établissement"}.
            </p>
          )}
          {events && events.length > 0 && (
            <ol className="space-y-3">
              {events.map((e) => {
                const dt = e.at ? new Date(e.at) : null;
                const dtLabel = dt ? dt.toLocaleString("fr-FR", {
                  day: "2-digit", month: "2-digit", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                }) : "—";
                return (
                  <li
                    key={e.id}
                    className="flex gap-3 border-l-2 pl-3 group"
                    style={{ borderColor: e.color || "#888" }}
                    data-testid={`history-event-${e.type}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-ink leading-tight" style={{ color: e.color || "#111" }}>
                        {e.label}
                      </p>
                      <p className="text-xs text-subtle mt-0.5">{dtLabel}</p>
                      {e.details && <p className="text-xs text-ink mt-1 leading-snug">{e.details}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => downloadOne(e.id)}
                      disabled={downloadingId === e.id}
                      className="self-start shrink-0 inline-flex items-center gap-1 h-7 px-2 rounded-md border border-border hover:border-brand text-[10px] font-bold uppercase tracking-wider text-subtle hover:text-ink disabled:opacity-50"
                      title="Télécharger cette ligne en PDF"
                      data-testid={`history-event-download-${e.id}`}
                    >
                      <FileDown size={12} />
                      {downloadingId === e.id ? "…" : "PDF"}
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
